/**
 * Permission helpers for IntelligenceBank assets
 * Handles permission checking and action availability
 */

/**
 * Standard IntelligenceBank permissions
 */
export type Permission =
  | 'resource_download'
  | 'resource_link'
  | 'resource_copy'
  | 'resource_edit'
  | 'view'
  | 'download'
  | 'list';

/**
 * Check if a specific permission is allowed
 * @param allowedActions - Array of allowed action strings from API
 * @param permission - Permission to check
 * @returns True if permission is allowed
 */
export function hasPermission(
  allowedActions: string[],
  permission: Permission
): boolean {
  if (!allowedActions || allowedActions.length === 0) {
    return false;
  }
  
  return allowedActions.includes(permission);
}

/**
 * Check if multiple permissions are allowed
 * @param allowedActions - Array of allowed action strings from API
 * @param permissions - Array of permissions to check
 * @returns True if ALL permissions are allowed
 */
export function hasAllPermissions(
  allowedActions: string[],
  permissions: Permission[]
): boolean {
  return permissions.every((permission) => hasPermission(allowedActions, permission));
}

/**
 * Check if any of the specified permissions are allowed
 * @param allowedActions - Array of allowed action strings from API
 * @param permissions - Array of permissions to check
 * @returns True if ANY permission is allowed
 */
export function hasAnyPermission(
  allowedActions: string[],
  permissions: Permission[]
): boolean {
  return permissions.some((permission) => hasPermission(allowedActions, permission));
}

/**
 * Get all available actions from allowed actions array
 * @param allowedActions - Array of allowed action strings from API
 * @returns Array of available Permission types
 */
export function getAvailableActions(allowedActions: string[]): Permission[] {
  if (!allowedActions || allowedActions.length === 0) {
    return [];
  }
  
  const validPermissions: Permission[] = [
    'resource_download',
    'resource_link',
    'resource_copy',
    'resource_edit',
    'view',
    'download',
    'list',
  ];
  
  return validPermissions.filter((permission) =>
    allowedActions.includes(permission)
  );
}

/**
 * Check if asset is publicly accessible
 * An asset is considered public if it has 'view' permission and is marked as public
 * @param isPublic - Public flag from asset
 * @param allowedActions - Array of allowed action strings from API
 * @returns True if asset is publicly accessible
 */
export function isPublicAsset(
  isPublic: boolean,
  allowedActions: string[]
): boolean {
  return isPublic && hasPermission(allowedActions, 'view');
}

/**
 * Check if asset is restricted (not public or has limited permissions)
 * @param isPublic - Public flag from asset
 * @param allowedActions - Array of allowed action strings from API
 * @returns True if asset is restricted
 */
export function isRestrictedAsset(
  isPublic: boolean,
  allowedActions: string[]
): boolean {
  // Asset is restricted if:
  // 1. Not public
  // 2. Has no allowed actions
  // 3. Only has 'list' permission
  return (
    isPublic === false ||
    allowedActions.length === 0 ||
    (allowedActions.length === 1 && allowedActions.includes('list'))
  );
}

/**
 * Check if download is available for the asset
 * @param allowedActions - Array of allowed action strings from API
 * @param isWebApp - True if running in web app mode
 * @returns True if download is available
 */
export function canDownload(
  allowedActions: string[],
  isWebApp: boolean = false
): boolean {
  if (isWebApp) {
    return hasPermission(allowedActions, 'resource_download');
  }
  return hasPermission(allowedActions, 'download');
}

/**
 * Check if public link generation is available
 * @param isPublic - Public flag from asset
 * @param allowedActions - Array of allowed action strings from API
 * @param isWebApp - True if running in web app mode
 * @returns True if public link can be generated
 */
export function canGeneratePublicLink(
  isPublic: boolean,
  allowedActions: string[],
  isWebApp: boolean = false
): boolean {
  if (isWebApp) {
    return hasPermission(allowedActions, 'resource_link') && !isRestrictedAsset(isPublic, allowedActions);
  }
  return isPublic && hasPermission(allowedActions, 'view');
}

/**
 * Check if copy to clipboard is available
 * @param allowedActions - Array of allowed action strings from API
 * @returns True if copy is available
 */
export function canCopy(allowedActions: string[]): boolean {
  return hasPermission(allowedActions, 'resource_copy');
}

/**
 * Check if asset can be edited
 * @param allowedActions - Array of allowed action strings from API
 * @returns True if edit is available
 */
export function canEdit(allowedActions: string[]): boolean {
  return hasPermission(allowedActions, 'resource_edit');
}

/**
 * Get permission summary for debugging
 * @param allowedActions - Array of allowed action strings from API
 * @param isPublic - Public flag from asset
 * @returns Object with permission flags
 */
export function getPermissionSummary(
  allowedActions: string[],
  isPublic: boolean = false
) {
  return {
    canDownload: canDownload(allowedActions, false),
    canGeneratePublicLink: canGeneratePublicLink(isPublic, allowedActions, false),
    canCopy: canCopy(allowedActions),
    canEdit: canEdit(allowedActions),
    isPublic: isPublicAsset(isPublic, allowedActions),
    isRestricted: isRestrictedAsset(isPublic, allowedActions),
    availableActions: getAvailableActions(allowedActions),
  };
}